const splitDataList = (value) => value ? value.split('|') : null;

const normalizeHolidays = (holidays) => {
  if (Array.isArray(holidays)) {
    return holidays;
  }

  if (!holidays || typeof holidays !== 'object') {
    return [];
  }

  return Object.entries(holidays).map(([date, translations]) => ({
    date,
    ...translations,
  }));
};

export function setupDashboardCalendar(element) {
  if (!element) return null;

  return dashboardCalendar({
    lang: element.dataset.dashboardCalendarLang,
    monthNames: splitDataList(element.dataset.dashboardCalendarMonthNames),
    dayNames: splitDataList(element.dataset.dashboardCalendarDayNames),
    headerText: element.dataset.dashboardCalendarHeaderText,
  });
}

export function dashboardCalendar(config = {}) {
  const defaults = {
    lang: 'en',
    monthNames: null,
    dayNames: null,
    headerText: '',
  };

  const options = Object.assign({}, defaults, config);

  // Funkcja pomocnicza do formatowania daty w formacie YYYY-MM-DD
  function formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  async function holidaysWithDesc() {
    try {
      const res = await fetch('/utils/daysOff', {
        method: 'GET',
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch /utils/daysOff: ${res.status}`);
      }

      const data = await res.json();
      return normalizeHolidays(data);
    } catch (e) {
      // fallback: brak danych dni wolnych, ale nadal uwzględniaj dodatkowe dni z configu
      return [];
    }
  }

  // Funkcja sprawdzająca, czy dwie daty są takie same
  function isSameDay(date1, date2) {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  // Generowanie HTML z najbliższymi 30 dniami
  async function generateHTML() {
    const col = document.createElement('div');
    col.classList.add('col');
    const container = document.createElement('div');
    container.classList.add('dashboardCalendar', 'h-100');
    const latestProducts = document.querySelector('#panel_latest_products');
    const today = new Date();
    const startOfWeek = new Date(today);

    // Przesuń na poniedziałek
    startOfWeek.setDate(today.getDate() - (today.getDay() === 0 ? 6 : today.getDay() - 1));

    let html = '';
    let currentYear = null;
    let currentMonth = null;

    // Domyślne nazwy miesięcy i dni tygodnia
    const defaultMonthNames = Array.from({ length: 12 }, (_, i) =>
      new Date(2000, i).toLocaleString('default', { month: 'long' }));
    const defaultDayNames = Array.from({ length: 7 }, (_, i) =>
      new Date(2000, 0, i + 3).toLocaleString('default', { weekday: 'short' }));

    const months = options.monthNames || defaultMonthNames;
    const days = options.dayNames || defaultDayNames;
    let addWeekdays = true;
    let addHeader = true;

    // Pobierz dni wolne tylko raz (unikamy await w pętli)
    const holidays = await holidaysWithDesc();
    const holidaysByDate = new Map(holidays.map((h) => [h.date, h]));
    const holidayDates = new Set(holidays.map((h) => h.date));

    for (let i = 0; i < 30; i++) {
      const currentDate = new Date(startOfWeek);
      currentDate.setDate(startOfWeek.getDate() + i);

      const formattedYear = currentDate.getFullYear();
      const formattedMonth = String(currentDate.getMonth() + 1).padStart(2, '0');
      const formattedDay = String(currentDate.getDate()).padStart(2, '0');
      const formattedCurrentDate = `${formattedYear}-${formattedMonth}-${formattedDay}`;

      const { headerText } = options;

      const year = currentDate.getFullYear();
      const month = months[currentDate.getMonth()];
      const day = String(currentDate.getDate()).padStart(2, '0');
      const formattedDate = formatDate(currentDate);

      const dayOfWeek = currentDate.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const isHolidayDate = holidayDates.has(formattedCurrentDate);
      const isPast = currentDate < today && !isSameDay(currentDate, today);
      const isToday = isSameDay(currentDate, today);

      let hasTooltip = '';
      let className = '';
      if (isPast) {
        className = 'disabled';
      } else if (isHolidayDate) {
        const holidayObj = holidaysByDate.get(formattedCurrentDate);
        hasTooltip = holidayObj && holidayObj[options.lang];
        className = 'holiday';
        if (isToday) {
          className = 'holiday current';
        }
      } else if (isWeekend) {
        className = 'weekend';
        if (isToday) {
          className = 'weekend current';
        }
      } else {
        className = 'workday';
        if (isToday) {
          className = 'workday current';
        }
      }

      // Dodawanie nagłówka sekcji
      if (addHeader) {
        html += `<h1 class="year-name">${headerText}</h1>`;
        addHeader = false;
      }

      // Dodawanie nowego roku, jeśli zmienił się rok
      if (year !== currentYear) {
        if (currentYear !== null) {
          html += '</div></div>'; // Zamykanie poprzedniego roku/miesiąca
        }
        currentYear = year;
        html += '<div class="year">';
        currentMonth = null; // Reset miesiąca przy zmianie roku
      }

      // Dodawanie nowego miesiąca, jeśli zmienił się miesiąc
      if (month !== currentMonth) {
        if (currentMonth !== null) {
          html += '</div>'; // Zamykanie poprzedniego miesiąca
        }
        currentMonth = month;
        html += `<div class="month"><div class="d-flex w-100 text-start pt-16 px-4"><h3 class="month-name">${month}</h3><h2 class="year-name ms-auto">${year}</h2></div>`;

        // dodaj nazwy dni tygodnia, tylko za pierwszym miesiącem
        if (addWeekdays) {
          html += `
            <div class="weekdays">
              ${days.map((dayName) => `<span class="day">${dayName}</span>`).join('')}
            </div>
          `;
        }

        // Dodaj "puste dni", jeśli miesiąc nie zaczyna się od poniedziałku
        const firstDayOfMonth = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          1,
        );
        // getDay(): 0 = niedziela, 1 = poniedziałek, ..., 6 = sobota
        const jsWeekday = firstDayOfMonth.getDay();
        // Zamieniamy tak, aby poniedziałek = 0, ..., niedziela = 6
        const weekdayFromMonday = jsWeekday === 0 ? 6 : jsWeekday - 1;
        // Dodajemy puste dni przed pierwszym dniem miesiąca (wyrównanie kalendarza)
        if (!addWeekdays && weekdayFromMonday > 0) {
          html += '<div class="day"></div>'.repeat(weekdayFromMonday);
        }
        addWeekdays = false;
      }

      // Dodawanie dnia
      if (hasTooltip) {
        html += `<div class="day ${className}" 
                     data-date="${formattedDate}" 
                     data-bs-trigger="hover"
                     data-bs-toggle="tooltip"
                     data-bs-placement="top"
                     aria-label="${hasTooltip}" 
                     data-bs-original-title="${hasTooltip}">${day}</div>`;
      } else {
        html += `<div class="day ${className}" data-date="${formattedDate}">${day}</div>`;
      }
    }

    html += '</div>';
    container.innerHTML = html;

    // Inicjalizacja tooltipów Bootstrap (dla dni posiadających tooltip)
    const bootstrap = window.bs && window.bs.bootstrap;
    if (bootstrap && typeof bootstrap.Tooltip === 'function') {
      const tooltipElements = container.querySelectorAll('[data-bs-toggle="tooltip"]');

      tooltipElements.forEach((el) => {
        // zabezpieczenie przed wielokrotną inicjalizacją
        const existing = bootstrap.Tooltip.getInstance(el);
        if (existing) {
          existing.dispose();
        }
        new bootstrap.Tooltip(el);
      });
    }


    if (latestProducts) {
      if (latestProducts.firstElementChild) {
        latestProducts.removeChild(latestProducts.firstElementChild);
      }
      col.append(container);
      latestProducts.prepend(col);

      latestProducts.children[0].classList.add(
        'order-sm-1',
        'order-lg-2',
        'order-xl-3',
      );
      latestProducts.children[1].classList.remove('order-last');
      latestProducts.children[2].classList.remove('order-last');
      latestProducts.children[3].classList.remove('order-last');
      latestProducts.children[1].classList.add(
        'order-xl-0',
        'order-lg-0',
        'order-sm-0',
        'order-last',
      );
      latestProducts.children[2].classList.add(
        'order-xl-1',
        'order-lg-1',
        'order-sm-last',
      );
      latestProducts.children[3].classList.add('order-xl-2', 'order-sm-last');
    }
  }

  return generateHTML();
}
